/**
 * PDP-pris i DOM: canonical (Liquid `pricing-canonical` → `variant.canonical_pricing`), legacy pack-fallback,
 * og én HTML-model for pr. m²-linjen i m²-beregneren.
 * Variant-kontekst: `window.HFCProductPdpVariants` (product-pdp-orderability-ui.js) eller globale get_current_variant / resolve_selected_variant.
 * Load efter orderability-ui, m2-calculator-ui og _product.js (se main-product-blocks.liquid).
 */
(function () {
    let __canonicalPriceObserverSilent = false;
    let __canonicalPriceDomObserver = null;
    const price_format_cache = {
        format_string: null,
    };

    function get_money_format_string() {
        if (price_format_cache.format_string !== null) {
            return price_format_cache.format_string;
        }
        if (window.MinimogSettings && window.MinimogSettings.money_format) {
            price_format_cache.format_string = window.MinimogSettings.money_format;
        } else if (typeof Shopify !== 'undefined' && Shopify.money_format) {
            price_format_cache.format_string = Shopify.money_format;
        } else {
            price_format_cache.format_string = 'kr. {{ amount_with_comma_separator }}';
        }
        return price_format_cache.format_string;
    }

    function format_price_display_value(raw_value, options = {}) {
        const is_cents = !!options.is_cents;
        const numeric_value = Number(raw_value);
        if (Number.isNaN(numeric_value)) {
            return '';
        }
        const cents_value = is_cents ? Math.round(numeric_value) : Math.round(numeric_value * 100);
        if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
            try {
                return Shopify.formatMoney(cents_value, get_money_format_string());
            } catch (err) {
                /* fallback handled below */
            }
        }
        const locale_code = (typeof Shopify !== 'undefined' && Shopify.locale) ? Shopify.locale : 'da-DK';
        const normalized_value = cents_value / 100;
        const localized_number = normalized_value.toLocaleString(locale_code, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
        return `kr. ${localized_number}`;
    }

    function set_price_node_value(target_node, value, options = {}) {
        if (!target_node) {
            return;
        }
        target_node.innerHTML = format_price_display_value(value, options);
    }

    function apply_legacy_pack_price_to_dom(price_block, variant, regular_span, sale_span, compare_strike) {
        const discount_attr = price_block.getAttribute('data-collection-discount');
        const discount_percent_dom = discount_attr ? parseInt(discount_attr, 10) : 0;
        const discount_percent = discount_percent_dom > 0
            ? discount_percent_dom
            : (window.shopify_product && window.shopify_product.collection_discount_percent
                ? parseInt(window.shopify_product.collection_discount_percent, 10)
                : 0);
        const base_price = parseFloat(variant.per_pack_sales_price) || 0;
        if (!base_price) {
            return;
        }
        let final_price = base_price;
        if (discount_percent > 0) {
            final_price = base_price * (100 - discount_percent) / 100;
        }
        const price_cents = Number(variant.price);
        const compare_cents = Number(variant.compare_at_price);
        const has_real_sale = !!(compare_cents && price_cents && compare_cents > price_cents);

        if (discount_percent > 0) {
            if (has_real_sale) {
                if (!price_block.classList.contains('f-price--on-sale')) {
                    price_block.classList.add('f-price--on-sale');
                }
                set_price_node_value(sale_span, price_cents, { is_cents: true });
                set_price_node_value(regular_span, price_cents, { is_cents: true });
                set_price_node_value(compare_strike, compare_cents, { is_cents: true });
            } else {
                if (!price_block.classList.contains('f-price--on-sale')) {
                    price_block.classList.add('f-price--on-sale');
                }
                set_price_node_value(sale_span, final_price);
                set_price_node_value(regular_span, final_price);
                set_price_node_value(compare_strike, base_price);
            }
        } else {
            if (price_block.classList.contains('f-price--on-sale')) {
                price_block.classList.remove('f-price--on-sale');
            }
            set_price_node_value(regular_span, final_price);
            set_price_node_value(sale_span, final_price);
            if (compare_strike) {
                compare_strike.innerHTML = '';
            }
        }
    }

    /** Én HTML-streng til pr. m²-linje (m²-beregner + øvrige kald der forventer samme markup). */
    function getPricePerSquareMeterHtml(price_per_square_meter, price_per_square_meter_discount, options = {}) {
        price_per_square_meter = +price_per_square_meter;
        if (Number.isNaN(price_per_square_meter)) {
            return '';
        }
        if (options.presentation === 'figma-m2') {
            if (!Number.isFinite(price_per_square_meter)) {
                return '';
            }
            const format_amount = function (amount) {
                return amount.toLocaleString('da-DK', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                });
            };
            const render_amount = function (amount, class_name) {
                return '<span class="' + class_name + '"><span class="_price-per-square-meter-value">'
                    + format_amount(amount) + '</span> <span class="_price-per-square-meter-unit">kr./m<sup>2</sup></span></span>';
            };
            const discount_price = Number(price_per_square_meter_discount);
            if (Number.isFinite(discount_price) && discount_price > 0) {
                return render_amount(discount_price, '_price-per-square-meter _compare')
                    + render_amount(price_per_square_meter, '_price-per-square-meter-before');
            }
            return render_amount(price_per_square_meter, '_price-per-square-meter');
        }
        let new_price_per_square_meter = price_per_square_meter.toFixed(2).replace('.', ',');
        new_price_per_square_meter = new_price_per_square_meter.toLocaleString('da-DK', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
        price_per_square_meter_discount = +price_per_square_meter_discount;
        if (price_per_square_meter_discount > 0) {
            let new_price_per_square_meter_discount = price_per_square_meter_discount.toFixed(2).replace('.', ',');
            new_price_per_square_meter_discount = new_price_per_square_meter_discount.toLocaleString('da-DK', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
            return '<span class="_price-per-square-meter _compare">kr. ' + new_price_per_square_meter_discount + ' <span class="_price-per-square-meter-unit">pr. m<sup>2</sup></span></span><span class="_price-per-square-meter-before">kr. ' + new_price_per_square_meter + ' <span class="_price-per-square-meter-unit">pr. m<sup>2</sup></span></span>';
        }
        return '<span class="_price-per-square-meter"><span>kr. ' + new_price_per_square_meter + '</span> <span class="_price-per-square-meter-unit">pr. m<sup>2</sup></span></span>';
    }

    function displayM2CalculatorPricePerSquareMeter(price_per_square_meter, price_per_square_meter_discount_price) {
        const span = document.querySelector('#m2_price_calculator #price_per_square_meter_span');
        if (!span) {
            return;
        }
        const disc = price_per_square_meter_discount_price > 0 ? price_per_square_meter_discount_price : 0;
        span.innerHTML = getPricePerSquareMeterHtml(price_per_square_meter, disc, {
            presentation: span.closest('#m2_price_calculator.pdp-figma-m2-root') ? 'figma-m2' : 'legacy',
        });
    }

    function resolvePricingVariant(passed_variant) {
        if (passed_variant) {
            return passed_variant;
        }
        const V = window.HFCProductPdpVariants;
        if (window._last_selected_variant_id != null && V && typeof V.findVariantById === 'function') {
            const by_last_id = V.findVariantById(window._last_selected_variant_id);
            if (by_last_id) {
                return by_last_id;
            }
        }
        if (V && typeof V.getCurrentVariant === 'function') {
            const by_current = V.getCurrentVariant();
            if (by_current) {
                return by_current;
            }
        }
        if (V && typeof V.resolveSelectedVariant === 'function') {
            return V.resolveSelectedVariant();
        }
        if (typeof get_current_variant === 'function') {
            return get_current_variant();
        }
        return null;
    }

    function getCanonicalPriceBlocks() {
        return Array.from(document.querySelectorAll(
            '.main-product__block.main-product__block-price .f-price, .main-product__block.main-product__block-price_from .f-price'
        ));
    }

    function get_unit_label_html(unit_label) {
        if (unit_label === 'm2') {
            return 'pr. m<sup>2</sup>';
        }
        return 'pr. stk.';
    }

    function variant_uses_m2_unit_label(variant) {
        const cp = variant && variant.canonical_pricing;
        if (cp && cp.unit_label) {
            return cp.unit_label === 'm2';
        }
        const mf = variant && variant.metafields;
        const m2_raw = mf && mf.price_per_square_meter;
        return m2_raw != null && m2_raw !== '' && m2_raw !== 0 && String(m2_raw) !== '0';
    }

    function sync_price_unit_label_from_variant(price_block, variant) {
        const container = price_block && price_block.querySelector('._custom-price-per-piece-container');
        if (!container || !variant) {
            return;
        }
        const cp = variant.canonical_pricing;
        if (cp && cp.unit_label) {
            container.innerHTML = get_unit_label_html(cp.unit_label);
            return;
        }
        container.innerHTML = get_unit_label_html(variant_uses_m2_unit_label(variant) ? 'm2' : 'stk');
    }

    function applyCanonicalPricingToPriceBlock(price_block, variant, should_fade) {
        if (!price_block || !variant) {
            return;
        }

        const regular_span = price_block.querySelector('.f-price__regular .f-price-item--regular')
            || price_block.querySelector('.f-price__regular span:not(.visually-hidden)');
        const sale_span = price_block.querySelector('.f-price__sale .f-price-item--sale');
        const compare_strike = price_block.querySelector('.f-price__sale s');

        const update_prices = () => {
            __canonicalPriceObserverSilent = true;
            try {
                const cp = variant.canonical_pricing;
                if (cp && typeof cp.display_price_cents === 'number') {
                    const disp = Math.round(cp.display_price_cents);
                    const disc = cp.display_compare_price_cents != null ? Math.round(cp.display_compare_price_cents) : 0;
                    const on_sale = disc > disp;
                    if (cp.price_mode) {
                        price_block.setAttribute('data-canonical-price-mode', cp.price_mode);
                    }
                    price_block.setAttribute('data-display-price-cents', String(disp));
                    price_block.setAttribute('data-display-compare-cents', String(disc));
                    if (typeof cp.collection_discount_percent === 'number') {
                        price_block.setAttribute('data-collection-discount', String(cp.collection_discount_percent));
                    }
                    if (on_sale) {
                        price_block.classList.add('f-price--on-sale');
                    } else {
                        price_block.classList.remove('f-price--on-sale');
                    }
                    set_price_node_value(sale_span, disp, { is_cents: true });
                    set_price_node_value(regular_span, disp, { is_cents: true });
                    if (compare_strike) {
                        if (disc > 0 && disc > disp) {
                            set_price_node_value(compare_strike, disc, { is_cents: true });
                        } else {
                            compare_strike.innerHTML = '';
                        }
                    }
                    sync_price_unit_label_from_variant(price_block, variant);
                    if (window.HFCThemeCampaign) window.HFCThemeCampaign.updatePdp(price_block.closest('[data-hfc-campaign-prices]'), variant);
                    return;
                }
                apply_legacy_pack_price_to_dom(price_block, variant, regular_span, sale_span, compare_strike);
                sync_price_unit_label_from_variant(price_block, variant);
            } finally {
                requestAnimationFrame(() => {
                    __canonicalPriceObserverSilent = false;
                });
            }
        };

        if (should_fade) {
            const fade_out_ms = 200;
            const swap_window_ms = 650;
            const fade_in_ms = 220;

            price_block.style.transition = 'opacity ' + fade_out_ms + 'ms ease, transform ' + fade_out_ms + 'ms ease';
            price_block.style.willChange = 'opacity, transform';
            price_block.style.transformOrigin = 'left center';
            price_block.style.position = 'relative';
            price_block.style.top = '0';
            price_block.style.verticalAlign = 'top';
            price_block.style.pointerEvents = 'none';
            price_block.style.transform = 'translate3d(-12px, 0, 0)';
            price_block.style.opacity = '0';

            setTimeout(() => { update_prices(); }, fade_out_ms + 20);

            setTimeout(() => {
                update_prices();
                price_block.style.transition = 'opacity ' + fade_in_ms + 'ms ease, transform ' + fade_in_ms + 'ms ease';
                price_block.style.transform = 'translate3d(0, 0, 0)';
                price_block.style.opacity = '1';
                setTimeout(() => { price_block.style.pointerEvents = ''; }, fade_in_ms + 40);
            }, swap_window_ms);
        } else {
            update_prices();
        }
    }

    function applyCanonicalPricingToDom(passed_variant = null, should_fade = false) {
        const price_blocks = getCanonicalPriceBlocks();
        if (!price_blocks.length) {
            return;
        }

        const variant = resolvePricingVariant(passed_variant);
        if (!variant) {
            return;
        }

        price_blocks.forEach(function (price_block) {
            applyCanonicalPricingToPriceBlock(price_block, variant, should_fade);
        });
    }

    function ensureCanonicalPriceDomObserver() {
        const price_blocks = getCanonicalPriceBlocks();
        if (!price_blocks.length || __canonicalPriceDomObserver) {
            return;
        }
        let debounce_timer = null;
        __canonicalPriceDomObserver = new MutationObserver(() => {
            if (__canonicalPriceObserverSilent) {
                return;
            }
            clearTimeout(debounce_timer);
            debounce_timer = setTimeout(() => {
                try {
                    const V = window.HFCProductPdpVariants;
                    const last_id = window._last_selected_variant_id != null ? String(window._last_selected_variant_id) : null;
                    const match = last_id && V && typeof V.findVariantById === 'function'
                        ? V.findVariantById(last_id)
                        : null;
                    if (match) {
                        applyCanonicalPricingToDom(match, false);
                    }
                } catch (e) { /* noop */ }
            }, 60);
        });
        price_blocks.forEach(function (price_block) {
            __canonicalPriceDomObserver.observe(price_block, { childList: true, subtree: true, characterData: true });
        });
    }

    window.HFCProductPdpPricingUi = {
        applyCanonicalPricingToDom,
        ensureCanonicalPriceDomObserver,
        getPricePerSquareMeterHtml,
        displayM2CalculatorPricePerSquareMeter,
    };
})();
